import { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { PageHead } from "src/molecules/PageHead";
import { t } from "@lingui/macro";
import { useRouter } from "next/router";
import { Heading, Text } from "@otrium/atoms";
import { Flex } from "@otrium/core";
import { useStorefrontCollection } from "src/hooks/storefrontCollection";
import { AppCtx } from "src/contexts/app.context";
import Error from "pages/_error";
import { PLPType } from "src/types/PLPType.d";
import CollectionsViewSkeleton from "src/molecules/CollectionsViewSkeleton/CollectionsViewSkeleton";
import { AlgoliaProductList } from "src/organisms/AlgoliaProductList";
import { setGender } from "src/modules/page";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import { useLingui } from "@lingui/react";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import {
  CustomContent,
  Image,
  StyledCollectionsCoverContainer,
  StyledSkeleton,
} from "./CollectionsView.styled";
import { CONSCIOUS_FACET, STYLE_FACET } from "src/utils/algolia";
import { theme } from "src/theme";

function CollectionView(): JSX.Element {
  const router = useRouter();
  const dispatch = useDispatch();

  const { locale, setPageViewedProperties } = useContext(AppCtx);
  const {
    collectionSlug,
    slug: genderId,
    discount,
  }: {
    slug?: string;
    collectionSlug?: string[];
    discount?: string;
  } = router.query;
  const { i18n } = useLingui();

  const slug = collectionSlug ? collectionSlug[0] : "";

  const { data: collection, error, loading } = useStorefrontCollection(slug);
  const { enablePLPIndexation } = useFeatureFlags();

  useEffect(() => {
    setPageViewedProperties({
      page_type: "collections",
      collection: collection?.name,
      collection_id: collection?.id,
    });
  }, [setPageViewedProperties, collection?.name, collection?.id]);

  useEffect(() => {
    const localeGenderId = localeFix({ gender: genderId });
    dispatch(setGender(localeGenderId));
  }, [dispatch, genderId]);

  if (!loading && error) {
    return <Error />;
  }

  const description = t(
    i18n
  )`Browse all items in ${collectionSlug} for ${genderId}. Shop top brands with discounts of up to 70%. Take advantage and order today!`;

  return (
    <>
      <PageHead
        title={t(i18n)`Collections | Otrium`}
        deeplink={`otrium://collections/${genderId}/${collectionSlug}${
          discount ? `?discount=${discount}` : ""
        }`}
        description={description}
        isPDP={!!enablePLPIndexation}
      />

      {(!collection || loading) && (
        <>
          <StyledSkeleton />
          <CollectionsViewSkeleton />
        </>
      )}

      {collection && (
        <CustomContent as="main">
          {collection.desktopImage && (
            <StyledCollectionsCoverContainer minimizeHeightOnMobile={true}>
              <Image src={collection.desktopImage} />
            </StyledCollectionsCoverContainer>
          )}

          {collection.title ? (
            <Flex
              flexDirection="column"
              alignItems="center"
              backgroundColor="tone.sand"
              padding={["space24", "space24", "space48"]}
              gap={theme.space.space8}
              marginTop={
                collection.desktopImage ? ["-24px", "-24px", "-48px"] : "0"
              }
            >
              <Heading
                as="h2"
                variant="h2"
                fontFamily="PlusJakartaSans"
                fontWeight="700"
                fontSize={["fontSize24", "fontSize24", "fontSize32"]}
                lineHeight={["32px", "40px"]}
                textAlign="center"
              >
                {collection.title}
              </Heading>
              {collection.subtitle && (
                <Text
                  fontSize="fontSize16"
                  color="tone.anthraciteLight"
                  lineHeight="24px"
                  textAlign="center"
                >
                  {collection.subtitle}
                </Text>
              )}
            </Flex>
          ) : null}

          {collection?.id && (
            <AlgoliaProductList
              hideCategoryTree
              collectionId={collection.id}
              genderId={genderId as string}
              locale={locale}
              plpType={PLPType.COLLECTIONS_PLP}
              excludedFacetsList={[STYLE_FACET, CONSCIOUS_FACET]}
            />
          )}
        </CustomContent>
      )}

      <RRPFootnote />
    </>
  );
}

export { CollectionView };
